// Centralized content & media — sourced from CASNAGGI ORIGINAL.pdf (Jan 2026)
export const ORG = {
  name: "Care Support for the Needy and Good Governance Initiative",
  acronym: "CASNAGGI",
  tagline: "Compassion meets Governance.",
  founded: "21 August 2025",
  rc: "RC: 8722526",
  registrar: "Corporate Affairs Commission (CAC)",
  address: "49 Customs Road, Bayelsa State, Nigeria",
  branches: "Suit 1B First Floor Bowl Jolly Nyame Stadium Yola Road Jalingo, Taraba State",
  // Structured rather than pipe-joined strings: the previous single-string form
  // was interpolated straight into tel: and mailto: hrefs, which produced a
  // malformed URI everywhere it was used. `whatsapp` is digits-only E.164
  // because wa.me silently fails on a leading + or any spaces.
  phones: [
    {
      label: "Primary",
      display: "+234 916 796 1355",
      tel: "+2349167961355",
      whatsapp: "2349167961355",
    },
    {
      label: "Secondary",
      display: "+234 813 741 5326",
      tel: "+2348137415326",
      whatsapp: "2348137415326",
    },
  ],
  emails: [
    { label: "General", address: "casnaggi@gmail.com" },
    { label: "Make a Wish", address: "makeawish@caresupportfortheneedy.com" },
  ],
  website: "www.casnaggi.org",
  socials: [
    { label: "Facebook", href: "https://web.facebook.com/casnaggi" },
    { label: "Twitter", href: "https://twitter.com/casnaggi" },
    { label: "Instagram", href: "https://instagram.com/casnaggi" },
    { label: "Youtube", href: "https://youtube.com/casnaggi" },
  ],
};

// Canonical "primary" accessors, so call sites don't repeat [0] everywhere.
ORG.primaryPhone = ORG.phones[0];
ORG.primaryEmail = ORG.emails[0];
ORG.whatsappHref = `https://wa.me/${ORG.primaryPhone.whatsapp}?text=${encodeURIComponent(
  "Hello CASNAGGI, I'd like to ask about "
)}`;

// Self-hosted from frontend/public/images, served by Netlify's CDN alongside
// the site. Previously these hotlinked the old WordPress install, which made
// the redesigned site depend on the old one staying online. Re-encoded as
// WebP at display size: 11.7 MB of source PNG/JPEG became 789 KB.
export const LOGO_URL = "/images/logo.webp";

export const MEDIA = {
  heroHome:
    "https://images.pexels.com/photos/28702872/pexels-photo-28702872.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400",
  unveiling: "/images/unveiling.webp",
  humanitarian: "/images/humanitarian.webp",
  youth:
    "https://images.unsplash.com/photo-1649486116205-71c92cf5160f?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1400",
  women:
    "https://images.pexels.com/photos/36660576/pexels-photo-36660576.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400",
  governance:
    "https://images.pexels.com/photos/6565752/pexels-photo-6565752.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400",
  publicService:
    "https://images.pexels.com/photos/35744764/pexels-photo-35744764.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400",
  story:
    "https://images.unsplash.com/photo-1610473068575-4d30e6390b1c?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1400",
  volunteer:
    "https://images.unsplash.com/photo-1593113598332-cd288d649433?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1400",
  medical:
    "https://images.unsplash.com/photo-1584515933487-779824d29309?auto=format&fit=crop&w=1400&q=80",
  dialogue:
    "https://images.pexels.com/photos/8850706/pexels-photo-8850706.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400",
  peacebuilding:
    "https://images.pexels.com/photos/6646868/pexels-photo-6646868.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400",
  workshop:
    "https://images.pexels.com/photos/8761744/pexels-photo-8761744.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400",
  food: "/images/food.webp",
  team: {
    ifeoma: "/images/team-ifeoma.webp",
    eucharia: "/images/team-eucharia.webp",
    chima: "/images/team-chima.webp",
    mbara: "/images/team-mbara.webp",
    blessing: "/images/team-blessing.webp",
    favour: "/images/team-favour.webp",
    bagudu: "/images/team-bagudu.webp",
  },
  programImages: {
    humanitarian: "/images/program-humanitarian.webp",
    youth: "/images/program-youth.webp",
    women: "/images/program-women.webp",
  },
};

// Hero carousel slides (for Home page)
export const HERO_SLIDES = [
  {
    image: MEDIA.heroHome,
    eyebrow: "CASNAGGI · Est. 21 August 2025",
    headline: "Compassion meets",
    headlineAccent: "Governance.",
    body: "A registered Nigerian NGO (RC: 8722526) advancing humanitarian care and good governance side-by-side—community by community.",
  },
  {
    image: MEDIA.women,
    eyebrow: "Women Empowerment",
    headline: "She leads.",
    headlineAccent: "Nigeria rises.",
    body: "Through She Leads, Women in Enterprise, and Safe Spaces, we're backing the women who will shape Nigeria's next decade.",
  },
  {
    image: MEDIA.humanitarian,
    eyebrow: "Humanitarian Relief",
    headline: "1,200 food packs.",
    headlineAccent: "650 lives treated.",
    body: "From flood response in Bayelsa and Niger to free medical outreach in Minna and Yenagoa—relief where it's needed most.",
  },
  {
    image: MEDIA.youth,
    eyebrow: "Youth in Governance",
    headline: "The next generation,",
    headlineAccent: "accountable.",
    body: "The Youth for Good Governance Fellowship is training 30 fellows across 3 states to lead with integrity.",
  },
];

export const NAV = [
  { label: "About", to: "/about", testid: "nav-about" },
  { label: "Programs", to: "/programs", testid: "nav-programs" },
  { label: "Events", to: "/events", testid: "nav-events" },
  { label: "Impact", to: "/impact", testid: "nav-impact" },
  { label: "Get Involved", to: "/get-involved", testid: "nav-get-involved" },
  { label: "Contact", to: "/contact", testid: "nav-contact" },
];

export const CORE_VALUES = [
  { title: "Compassion", body: "Prioritizing care and support for the needy." },
  { title: "Integrity", body: "Upholding honesty, accountability, and transparency in all engagements." },
  { title: "Equity", body: "Promoting fairness, justice, and inclusiveness across all societal groups." },
  { title: "Empowerment", body: "Building the capacity of youth, women, and senior citizens to be agents of change." },
  { title: "Good Governance", body: "Advocating for leadership that is accountable, transparent, and people-centered." },
  { title: "Collaboration", body: "Partnering with stakeholders, communities, and institutions for collective impact." },
];

// 7 thematic areas with named sub-programs
export const PROGRAMS = [
  {
    slug: "humanitarian-care",
    title: "Humanitarian Care & Social Support",
    short:
      "Food, clothing, hygiene and psychosocial relief for Nigeria's most vulnerable communities.",
    bullets: [
      "Community Relief Outreach — food, clothing and hygiene kits for vulnerable households.",
      "Health on Wheels — mobile clinics for rural and underserved communities.",
      "Hope & Healing Initiative — psychosocial support for widows, orphans and displaced persons.",
    ],
    image: MEDIA.humanitarian,
  },
  {
    slug: "youth-empowerment",
    title: "Youth Empowerment & Skills Development",
    short:
      "Training the next generation of Nigerian leaders in civic action, ICT and entrepreneurship.",
    bullets: [
      "Youth in Governance Fellowship — leadership, civic participation and policy advocacy training.",
      "Digital Skills for the Future — ICT, entrepreneurship and vocational training for employability.",
      "Youth Mentorship Hub — linking young leaders with mentors in governance and business.",
    ],
    image: MEDIA.youth,
  },
  {
    slug: "women-empowerment",
    title: "Women Empowerment & Gender Equality",
    short:
      "Building political voice, financial independence and safety for women across Nigeria.",
    bullets: [
      "She Leads Project — capacity-building and advocacy for women's political participation.",
      "Women in Enterprise (WiE) — training and micro-grant support for women-led businesses.",
      "Safe Spaces Initiative — support and advocacy for survivors of gender-based violence.",
    ],
    image: MEDIA.women,
  },
  {
    slug: "public-service",
    title: "Public Service & Civil Service Development",
    short:
      "Building a Nigerian civil service that serves with integrity, competence and accountability.",
    bullets: [
      "Integrity in Service Award — recognizing excellence, honesty and accountability.",
      "Civil Service Capacity Hub — policy development, service delivery and financial management training.",
      "Leadership for Governance Program — ethical and transparent leadership skills for public officers.",
    ],
    image: MEDIA.publicService,
  },
  {
    slug: "good-governance",
    title: "Good Governance & Accountability",
    short:
      "Arming citizens with knowledge and opening real channels of dialogue with policymakers.",
    bullets: [
      "Citizens' Rights Awareness Campaign — civic education on rights, duties and electoral participation.",
      "Transparency Watch — advocacy for anti-corruption reforms and open government.",
      "Community Dialogue Forums — bridging the gap between communities and policymakers.",
    ],
    image: MEDIA.governance,
  },
  {
    slug: "community-development",
    title: "Community Development & Livelihoods",
    short:
      "Clean water, food security and climate-smart livelihoods for resilient rural communities.",
    bullets: [
      "Clean Water for All — borehole and sanitation projects in rural communities.",
      "Agri-Support Program — training farmers in climate-smart agriculture and food security.",
      "Resilient Communities Initiative — strengthening grassroots structures for local development.",
    ],
    image: MEDIA.food,
  },
  {
    slug: "peacebuilding",
    title: "Peacebuilding & Social Justice",
    short:
      "Conflict mediation, interfaith dialogue and human rights advocacy for a peaceful Nigeria.",
    bullets: [
      "Peace Clubs in Schools — building inter-ethnic and inter-faith tolerance among young people.",
      "Dialogue for Peace — platforms for community mediation and inter-religious harmony.",
      "Justice for All Campaign — advocacy for fairness, equity and human rights protection.",
    ],
    image: MEDIA.peacebuilding,
  },
];

export const TRUSTEES = [
  {
    name: "Mrs. Ifeoma Okorie Ikoro",
    role: "Founder / CEO",
    photo: MEDIA.team.ifeoma,
    bio: "A dynamic leader, humanitarian and advocate for social justice — Founder/CEO of CASNAGGI. Renowned for blending compassion with accountability.",
    bioFull: [
      "Comrade Ifeoma Okorie Ikoro is a dynamic leader, humanitarian, and advocate for social justice, serving as Founder and CEO of Care Support for the Needy and Good Governance Initiative (CASNAGGI).",
      "Renowned for blending compassion with accountability, she has built CASNAGGI into a nationally recognized platform that empowers youth, uplifts women, strengthens institutions, and amplifies marginalized voices. With a rare ability to connect grassroots communities and policy leaders, she inspires change, champions transparency, and stands as a powerful voice for the voiceless—driving a vision of governance rooted in integrity, fairness, and inclusivity.",
      "Her leadership philosophy is grounded in the belief that true development must combine compassion with accountability. She has championed programs that not only provide immediate relief to the needy but also build the capacity of citizens and public service holders to demand and deliver better governance. Through her efforts, CASNAGGI has become both a humanitarian lifeline and a civic movement that bridges the gap between communities and policymakers.",
      "Comrade Ikoro is also known for her ability to inspire and mobilize. Her work reflects a rare blend of grassroots connection and policy-level influence, enabling her to engage effectively with both local communities and national stakeholders. Her dedication has earned her recognition as a voice for the voiceless and a role model for women in leadership.",
      "With a deep sense of purpose and resilience, she continues to steer CASNAGGI toward its vision of a society where the needs of the vulnerable are met and where governance is driven by integrity, fairness, and inclusivity. Her story embodies the power of leadership anchored in service, courage, and the pursuit of justice for all.",
    ],
  },
  {
    name: "Dr. Chima Ikoro",
    role: "Chairman, Board of Directors",
    photo: MEDIA.team.chima,
    bio: "Consultant Gynaecologist at the Federal Medical Centre, Yenagoa, and Director of MetrixMed Clinics — combining clinical expertise with visionary leadership.",
    bioFull: [
      "Dr. Chima Ikoro is a seasoned Consultant Gynaecologist at the Federal Medical Centre, Yenagoa, Bayelsa State, and the Director of MetrixMed Clinics — a private facility renowned for comprehensive healthcare.",
      "Under his leadership, MetrixMed offers a wide range of services including medicine, obstetrics and gynaecology, paediatrics, family planning, surgery, general medical practice, laboratory diagnostics, and ultrasound scanning.",
      "With a strong commitment to excellence and patient-centered care, Dr. Ikoro combines clinical expertise with visionary leadership, making MetrixMed a trusted hub for quality healthcare in Yenagoa. As Chairman of the CASNAGGI Board of Directors, he provides strategic oversight and chairs board deliberations, while anchoring the organization's Health on Wheels and Hope & Healing initiatives — personally leading maternal health outreaches in the field.",
    ],
  },
  {
    name: "Rev. Fr. Christian Mbara",
    role: "Director",
    photo: MEDIA.team.mbara,
    bio: "Catholic priest ordained in 2007, now serving in the Diocese of Charlottetown, Canada. A pastoral leader rooted in service, faith and compassion.",
    bioFull: [
      "Ordained in 2007, Rev. Fr. Christian Mbara is a dedicated priest of the Catholic Diocese of Okigwe, Nigeria, now serving in the Diocese of Charlottetown, Canada. A native of Nneato, Umunneochi, he studied philosophy and theology at Seat of Wisdom Major Seminary, Owerri, and earned a PGDE from Michael Okpara University, Umudike.",
      "Over the years, he has held key pastoral roles — including Parish Priest, Cathedral Administrator, Rector, and Dean — marked by his passion for prayer, preaching, charity, and compassionate care for the sick and needy.",
      "As a Director of CASNAGGI, he embodies a ministry rooted in service, faith, and pastoral compassion — anchoring the organization's ethical compass and its commitment to the dignity of every human being it serves.",
    ],
  },
  {
    name: "Engr. Favour Chigemezu Iwuchukwu",
    role: "National Coordinator",
    photo: MEDIA.team.favour,
    bio: "A visionary leader and programme manager with extensive experience in development, governance, and community empowerment. As National Coordinator, he oversees strategy, projects, and impact across all programme pillars, ensuring alignment with CASNAGGI’s mission of integrating humanitarian service with good governance.",
    bioFull: [
      "Engineer Favour Iwuchukwu is a purpose-driven leader, development advocate, and seasoned entrepreneur committed to advancing community impact and good governance in Nigeria. Hailing from Imo State, he holds a Higher National Diploma (HND) in Chemical Engineering from the Institute of Management and Technology (IMT), Enugu, where he built a strong technical foundation that complements his strategic leadership abilitiesAs the National Coordinator of the Care Support for the Needy and Good Governance Initiative (CASNAGGI), Engineer Favour Iwuchukwu champions initiatives that uplift vulnerable populations while fostering accountability, transparency, and citizen engagement in governance. His leadership reflects a deep passion for social equity and sustainable development.",
      "Beyond his national role, he serves as a Trustee on the Board of the International Standard Center for Development, where he contributes to shaping impactful development programs on a broader scale. His influence spans both grassroots mobilization and institutional development.",
      "A forward-thinking entrepreneur, he is the Director of Favour Food Grains Enterprise, where he merges innovation with social responsibility, using business as a tool for economic empowerment and community transformation.",
      "Engineer Favour Iwuchukwu stands at the intersection of engineering, leadership, and humanitarian service—dedicated to building a more inclusive and accountable society.",
    ],
  },
  {
    name: "Lady Eucharia Nganya",
    role: "Director",
    photo: MEDIA.team.eucharia,
    bio: "Retired educationist and humanitarian. Former Chief Supervisor of Schools with the State Primary Education Board, Owerri. A passionate servant of vulnerable communities.",
    bioFull: [
      "Lady Eucharia Nganya is a retired educationist, humanitarian, and Board of Trustees member of CASNAGGI. Born in 1963 in Ehime Mbano LGA, Imo State, she served meritoriously as Chief Supervisor of Schools with the State Primary Education Board, Owerri, before retiring in 2019.",
      "She is married to Sir Dennis Nganya and is blessed with children. Currently based in Owerri, Imo State, Nigeria.",
      "Mrs. Eucharia Nganya is a passionate humanitarian dedicated to creating meaningful impact in the lives of vulnerable communities. Known for her compassion and commitment to service, she channels her energy into initiatives that uplift the needy, empower women and youth, and promote social well-being.",
      "With a strong belief in the power of kindness and collective action, she continues to inspire change — embodying leadership rooted in empathy, resilience, and a vision for a more inclusive and compassionate society.",
    ],
  },
  {
    name: "Ms. Blessing Chinwe Okorie",
    role: "Director",
    photo: MEDIA.team.blessing,
    bio: "Senior Administrative Officer at BIODEC, Abagana. Health education specialist and public administration scholar with a passion for service.",
    bioFull: [
      "Blessing C. Okorie is a seasoned health and administrative professional, currently serving as Senior Administrative Officer at the Bioresources Development Centre (BIODEC), Abagana, Anambra State.",
      "With a strong background in health education, she previously worked with the Imo State Ministry of Health in the National Program on Immunization and at Ultimate Home Health Facility.",
      "She holds a B.Sc. in Health Education from the University of Nigeria, Nsukka (2006), a PGDE in Public Administration (2022), and is presently pursuing a Master's in Public Administration. A dedicated member of NASU Abagana.",
      "Blessing combines expertise in healthcare and administration with a passion for service, making her a dynamic force in both public health and organizational leadership. On the CASNAGGI board, she anchors our health-linked programs and human resources governance.",
    ],
  },
  {
    name: "Dr. Bagudu S. Ezekiel",
    role: "Director of Programs",
    photo: MEDIA.team.bagudu,
    bio: "A development professional with over a decade of experience in humanitarian and community development work. Leads program design, implementation, and monitoring across West Africa.",
    bioFull: [
      "Mr. Bagudu S. Ezekiel is a development professional with over a decade of experience in humanitarian and community development work. He currently serves as Director of Programs at CASNAGGI, where he is responsible for shaping and monitoring impactful initiatives that empower communities and advance good governance.",
      "Reporting to the National Coordinator, he ensures programs align with CASNAGGI's mission,  bridging grassroots needs with policy advocacy to deliver lasting change.",
    ],
  },
];

export const ETHICS = [
  {
    title: "Integrity and Accountability",
    body: "Operate with honesty, transparency and fairness. Ensure responsible use of resources; hold leadership and partners accountable for their actions.",
  },
  {
    title: "Respect for Human Dignity",
    body: "Treat every individual with respect regardless of ethnicity, gender, religion or status. Uphold confidentiality and safeguard the dignity of vulnerable groups.",
  },
  {
    title: "Commitment to Good Governance",
    body: "Advocate for fairness, justice and accountability. Lead by example with organizational transparency and promote civic responsibility.",
  },
  {
    title: "Professionalism and Excellence",
    body: "Deliver programs with competence, dedication and efficiency. Promote continuous learning, capacity building and innovation.",
  },
  {
    title: "Non-Partisanship and Neutrality",
    body: "Maintain independence from political affiliations. Avoid favoritism; stay focused on humanitarian and governance objectives.",
  },
  {
    title: "Partnership and Collaboration",
    body: "Build trust-based relationships with communities, government, civil society and international agencies. Foster teamwork and open dialogue.",
  },
  {
    title: "Stewardship and Sustainability",
    body: "Manage financial, human and natural resources responsibly. Prioritize long-term community benefit and environmental sustainability.",
  },
];

export const OBJECTIVES = {
  Humanitarian: [
    "Provide care and humanitarian support for vulnerable groups including orphans, widows, IDPs and marginalized communities.",
    "Support community development initiatives — clean water, sanitation, food security and sustainable livelihoods.",
    "Foster peacebuilding and social justice through conflict resolution and human rights campaigns.",
  ],
  Empowerment: [
    "Empower youth and women through skills acquisition, entrepreneurship, education and leadership training.",
    "Promote democratic participation through civic education and dialogue platforms.",
    "Advocate for gender equality and women's inclusion in governance and decision-making.",
  ],
  Governance: [
    "Strengthen public and civil service systems through ethical leadership and accountability.",
    "Build partnerships with government, civil society, private sector and international organizations.",
    "Conduct research and evidence-based advocacy to influence social, economic and governance policy.",
    "Recognize and celebrate institutions that exemplify transparency and service to humanity.",
  ],
};

// Real 2025 impact numbers sourced from PDF report
export const IMPACT_STATS = [
  { value: "5,000+", label: "Lives touched directly in 2025" },
  { value: "1,200", label: "Food relief packs distributed (Bayelsa & Niger)" },
  { value: "650", label: "Beneficiaries of free medical outreach" },
  { value: "15,000", label: "Nigerians reached via #CASNAGGIVoice campaign" },
];

export const IMPACT_HIGHLIGHTS = [
  { value: "150", label: "Women & youth trained in livelihood skills" },
  { value: "45", label: "Start-up kits distributed (sewing, catering, ICT)" },
  { value: "30", label: "Youth for Good Governance Fellows across 3 states" },
  { value: "3,000", label: "Citizens sensitized on voting rights & accountability" },
  { value: "50", label: "Community volunteers mobilized as focal points" },
  { value: "2", label: "Governance town halls with policymakers" },
];

// Events / Humanitarian Outreaches (posted field reports)
export const EVENTS = [
  {
    slug: "cash-gifts-nobis-hall-jalingo",
    date: "2026-01-15",
    dateLabel: "January 2026",
    category: "Humanitarian Care",
    location: "Nobis Hall, Jalingo, Taraba State",
    title: "Cash Gifts for Vulnerable Families & Orphans",
    excerpt:
      "A compassionate January gathering at Nobis Hall where CASNAGGI distributed cash gifts to orphans and vulnerable families, restoring a little hope at the start of the year.",
    image: MEDIA.food,
    tags: ["Cash Relief", "Orphans", "Jalingo"],
    story: [
      "On a crisp January morning in Taraba State, CASNAGGI gathered at Nobis Hall, Jalingo, for what began as a simple cash-gift distribution — and quickly became a statement about the kind of Nigeria we are building, one where the most vulnerable are seen, counted and supported.",
      "Orphans, widows and struggling families received direct cash gifts to help cover school fees, food and essential household needs. For many, the assistance arrived at the exact moment the new year's pressures were settling in.",
      "Our volunteers also spent time listening — recording each household's situation so we can layer in follow-up support throughout 2026 through the Hope & Healing Initiative and the Community Relief Outreach.",
    ],
    stats: [
      { value: "120+", label: "Families directly supported" },
      { value: "45", label: "Orphans received cash gifts" },
      { value: "1", label: "Day of distribution" },
    ],
  },
  {
    slug: "community-relief-taraba",
    date: "2025-12-10",
    dateLabel: "December 2025",
    category: "Humanitarian Care",
    location: "Taraba State",
    title: "Community Relief Outreach – Taraba State",
    excerpt:
      "Over 500 families supported with food items, hygiene kits and essentials. Women-led groups received starter packs to launch small businesses.",
    image: MEDIA.humanitarian,
    tags: ["Relief", "Women-led", "500 families"],
    story: [
      "Taraba State welcomed CASNAGGI's largest humanitarian drive of 2025. Over the course of the outreach, more than 500 vulnerable households received food items, hygiene kits and essential supplies calibrated to last a family of five through a full month.",
      "We intentionally designed the event around women-led cooperatives. Alongside relief packages, we distributed starter packs — sewing machines, catering equipment and capital stock — to 45 women ready to turn their skills into income. It's our conviction that relief works best when paired with the tools of independence.",
      "Community leaders, traditional rulers and local policymakers joined the closing session, turning a distribution event into a public accountability moment.",
    ],
    stats: [
      { value: "500+", label: "Families supported" },
      { value: "45", label: "Women-led starter packs" },
      { value: "3", label: "Local government areas reached" },
    ],
  },
  {
    slug: "free-medical-outreach-minna-yenagoa",
    date: "2025-11-20",
    dateLabel: "November 2025",
    category: "Health on Wheels",
    location: "Minna & Yenagoa",
    title: "Free Medical Outreach — Minna & Yenagoa",
    excerpt:
      "650 beneficiaries received maternal health services, malaria screening and free essential drugs across two days of mobile clinics.",
    image: MEDIA.medical,
    tags: ["Health", "Maternal", "650 beneficiaries"],
    story: [
      "Our flagship Health on Wheels programme made landfall in two states — Niger and Bayelsa — delivering free primary care to communities far from the nearest clinic.",
      "650 people received services ranging from malaria screening and anti-malarial treatment to antenatal checks, blood pressure monitoring and diabetes screening. Essential drugs were provided at no cost, with follow-up referrals for patients needing specialist care.",
      "Dr. Chima Ikoro, CASNAGGI Board Chairman and Consultant Gynaecologist at Federal Medical Centre Yenagoa, led the maternal health station — a reminder that our board is not just strategic but hands-on.",
    ],
    stats: [
      { value: "650", label: "Beneficiaries treated" },
      { value: "2", label: "States reached" },
      { value: "12", label: "Medical professionals deployed" },
    ],
  },
  {
    slug: "youth-governance-forum",
    date: "2025-10-15",
    dateLabel: "October 2025",
    category: "Youth Empowerment",
    location: "Bayelsa · Niger · FCT",
    title: "Youth for Good Governance Forum (YG2F)",
    excerpt:
      "200+ young people engaged in civic education, leadership training and mentorship. 30 fellows selected for the inaugural Youth for Good Governance Fellowship.",
    image: MEDIA.youth,
    tags: ["Youth", "Civic", "200 engaged"],
    story: [
      "We don't believe Nigeria's youth are 'leaders of tomorrow' — we believe they are leaders right now. The Youth for Good Governance Forum was designed to give them the floor.",
      "Over 200 young people from Bayelsa, Niger and the FCT converged for two days of civic education, leadership training, and direct dialogue with policymakers. Workshops covered electoral participation, policy advocacy, digital organising and ethical leadership.",
      "At the forum's close, 30 outstanding participants were selected as the inaugural cohort of the Youth for Good Governance Fellowship (YG2F) — a 12-month programme pairing each fellow with a governance mentor.",
    ],
    stats: [
      { value: "200+", label: "Young people engaged" },
      { value: "30", label: "Fellows selected" },
      { value: "3", label: "States represented" },
    ],
  },
  {
    slug: "public-dialogue-transparency",
    date: "2025-09-25",
    dateLabel: "September 2025",
    category: "Good Governance",
    location: "Bayelsa State",
    title: "Public Dialogue on Transparency & Accountability",
    excerpt:
      "Over 100 citizens, women and civil servants joined an open-air dialogue with policymakers and traditional leaders on inclusive governance.",
    image: MEDIA.dialogue,
    tags: ["Governance", "100+ citizens", "Dialogue"],
    story: [
      "Under the open sky in Bayelsa State, CASNAGGI convened a cross-section of Nigerian society — community members, women's groups, traditional rulers, civil servants and elected officials — for a direct, unscripted dialogue on transparency and accountability.",
      "The session deliberately removed the usual barriers of protocol. Citizens asked pointed questions about service delivery, public spending and local development priorities. Officials responded, and — crucially — committed to follow-up actions monitored by CASNAGGI's Transparency Watch programme.",
      "Over 100 participants joined. Many stayed two hours past the scheduled close.",
    ],
    stats: [
      { value: "100+", label: "Citizens participated" },
      { value: "8", label: "Officials engaged on record" },
      { value: "2hr", label: "Extended Q&A session" },
    ],
  },
  {
    slug: "volunteer-mobilization-taraba-niger-fct",
    date: "2025-09-10",
    dateLabel: "September 2025",
    category: "Grassroots Network",
    location: "Taraba · Niger · FCT",
    title: "Grassroots Volunteer Mobilization",
    excerpt:
      "50 youth and women volunteers trained and onboarded as community focal points, strengthening CASNAGGI's grassroots presence across three states.",
    image: MEDIA.volunteer,
    tags: ["Volunteers", "50 mobilized", "3 states"],
    story: [
      "CASNAGGI's grassroots network tripled in a single week. 50 youth and women volunteers from Taraba, Niger State and the FCT were trained and deployed as community focal points — the people neighbours call first when they need help.",
      "Training covered outreach logistics, data collection, safeguarding, civic education facilitation and the CASNAGGI code of ethics. Every volunteer received a CASNAGGI-branded kit and a clear reporting structure to the state coordinator.",
      "The network will anchor our 2026 field operations across humanitarian response, civic education and women's enterprise.",
    ],
    stats: [
      { value: "50", label: "Volunteers trained & deployed" },
      { value: "3", label: "States activated" },
      { value: "100%", label: "Completed safeguarding certification" },
    ],
  },
  {
    slug: "skills-training-graduation",
    date: "2025-09-05",
    dateLabel: "September 2025",
    category: "Women Empowerment",
    location: "Bayelsa State",
    title: "150 Women & Youth Graduate Vocational Training",
    excerpt:
      "Tailoring, ICT and catering cohort graduates 150. 45 participants received sewing machines, hairdressing tools and catering equipment to begin earning immediately.",
    image: MEDIA.workshop,
    tags: ["Skills", "150 graduates", "Start-up kits"],
    story: [
      "Graduation days at CASNAGGI don't end with certificates. They end with tools.",
      "150 women and youth completed intensive vocational training across tailoring, ICT and catering streams. At the ceremony, 45 of the most entrepreneur-ready graduates received start-up kits — sewing machines, catering equipment and ICT bundles — enabling income generation from day one.",
      "This cohort represents the first wave of the She Leads Project x Women in Enterprise pipeline. Follow-up check-ins at 3, 6 and 12 months will track their business outcomes and loop the highest performers back in as peer mentors.",
    ],
    stats: [
      { value: "150", label: "Graduates" },
      { value: "45", label: "Start-up kits distributed" },
      { value: "3", label: "Vocational streams" },
    ],
  },
  {
    slug: "board-inaugural-strategy",
    date: "2025-08-28",
    dateLabel: "August 2025",
    category: "Organizational Milestone",
    location: "Head Office, Bayelsa",
    title: "Board of Trustees Inaugural Strategy Meeting",
    excerpt:
      "The CASNAGGI Board of Trustees convened its first strategy meeting, adopting the 2025–2035 Strategic Action Plan and operational governance framework.",
    image: MEDIA.governance,
    tags: ["Governance", "Strategic Plan"],
    story: [
      "Every national movement needs a serious first meeting. Ours happened one week after CAC registration, at the Bayelsa head office.",
      "Founder/CEO Comrade Ifeoma Okorie Ikoro, Board Chairman Dr. Chima Ikoro and the Directors convened for two days of strategic planning. The board adopted the 2025–2035 Strategic Action Plan, approved the governance and financial management policies, and set the operational calendar for the first 12 months.",
      "Minutes, resolutions and the adopted strategic document are archived with the Corporate Affairs Commission and available to institutional partners on request.",
    ],
    stats: [
      { value: "5", label: "Directors convened" },
      { value: "10", label: "Year strategic plan adopted" },
      { value: "7", label: "Policy documents ratified" },
    ],
  },
  {
    slug: "cac-registration",
    date: "2025-08-21",
    dateLabel: "21 August 2025",
    category: "Organizational Milestone",
    location: "Corporate Affairs Commission, Abuja",
    title: "CASNAGGI Officially Registered (RC: 8722526)",
    excerpt:
      "CASNAGGI secured legal registration with the Corporate Affairs Commission of Nigeria as a non-profit, non-governmental organization — our founding day.",
    image: MEDIA.publicService,
    tags: ["Milestone", "CAC", "NGO"],
    story: [
      "On 21 August 2025, Care Support for the Needy and Good Governance Initiative was formally incorporated with the Corporate Affairs Commission of Nigeria under registration number RC: 8722526.",
      "Registration formalised years of informal work into a national legal vehicle — enabling CASNAGGI to enter into institutional partnerships, receive grants and publish audited accounts under Nigerian non-profit regulations.",
      "It is also the date we now consider our founding anniversary — and a reminder that accountability starts with being a legally accountable entity before you ask others to be accountable.",
    ],
    stats: [
      { value: "RC: 8722526", label: "Registration number" },
      { value: "21 Aug", label: "Founding date" },
      { value: "NGO", label: "Legal personality" },
    ],
  },
];

// Strategic Action Plan 2025-2035 (summary)
export const STRATEGIC_PLAN = [
  {
    phase: "2025–2027",
    label: "Foundation",
    goals: [
      "Rapid response teams in Taraba, Bayelsa, Niger & FCT",
      "1,000 women and youth trained in vocational & digital skills",
      "Civic education campaigns in 5 pilot states",
      "300 civil servants trained on ethics and transparency",
    ],
  },
  {
    phase: "2028–2030",
    label: "Expansion",
    goals: [
      "Humanitarian hubs expanded to 10 states",
      "5 CASNAGGI Skill Acquisition & Leadership Centres",
      "National Civic Education Curriculum rolled out in schools",
      "15 state chapters with dedicated program officers",
    ],
  },
  {
    phase: "2031–2035",
    label: "Nationwide Scale",
    goals: [
      "Nationwide humanitarian reach with sustainable funding",
      "National Youth & Women Empowerment Fund — 20,000 beneficiaries",
      "National Civic Leadership Academy established",
      "Full national presence backed by an endowment fund",
    ],
  },
];

export const PROJECTED_2035 = [
  "50,000+ households reached with humanitarian support",
  "20,000 women and youth trained and empowered",
  "10,000 civil servants trained on governance ethics",
  "National Civic Education Curriculum adopted in schools",
  "CASNAGGI recognized as a leading NGO in governance advocacy in Nigeria",
];
